import React, { FC, ReactElement, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as SerbianFlag } from 'assets/images/serbian.svg';
import { ReactComponent as EnglishFlag } from 'assets/images/english.svg';
import { ReactComponent as SpanishFlag } from 'assets/images/spanish.svg';
import { ReactComponent as MontenegrinFlag } from 'assets/images/montenegrin.svg';
import { useCountryContext } from 'context/CountryProvider';
import { ELanguage, EStorageKey } from 'common/const';
import { useStorage } from 'common/hooks/useStorage';
import { getAvailableLanguages } from 'common/helpers/language.helper';

interface ILanguageOption {
  id: ELanguage;
  label: string;
  flag: ReactElement;
}

const languageOptionsById: Record<ELanguage, ILanguageOption> = {
  [ELanguage.SR]: { id: ELanguage.SR, label: 'Srpski', flag: <SerbianFlag /> },
  [ELanguage.EN]: { id: ELanguage.EN, label: 'English', flag: <EnglishFlag /> },
  [ELanguage.ES]: { id: ELanguage.ES, label: 'Español', flag: <SpanishFlag /> },
  [ELanguage.CNR]: { id: ELanguage.CNR, label: 'Crnogorski', flag: <MontenegrinFlag /> },
};

const fallbackOption = languageOptionsById[ELanguage.EN];

export const LanguageSwitcher: FC = () => {
  const { i18n } = useTranslation();
  const { country } = useCountryContext();
  const { setStorageItem } = useStorage();
  const [isOpen, setIsOpen] = useState(false);

  // Набор языков по действующей стране (US-034, R-1)
  const languageOptions = getAvailableLanguages(country).map((id) => languageOptionsById[id]);
  const containerRef = useRef<HTMLDivElement>(null);

  const currentOption = languageOptions.find(({ id }) => id === i18n.resolvedLanguage) ?? fallbackOption;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const onOutsideClick = (event: MouseEvent | TouchEvent) => {
      if (containerRef.current !== null && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', onOutsideClick);
    document.addEventListener('touchstart', onOutsideClick);
    document.addEventListener('keydown', onEscape);

    return () => {
      document.removeEventListener('mousedown', onOutsideClick);
      document.removeEventListener('touchstart', onOutsideClick);
      document.removeEventListener('keydown', onEscape);
    };
  }, [isOpen]);

  const onLanguageSelect = (id: ELanguage) => {
    // Отметку «язык выбрал гость» ставит только нажатие здесь, не автоопределение (US-034, R-2)
    setStorageItem(EStorageKey.LangChosen, 'true');
    i18n.changeLanguage(id);
    setIsOpen(false);
  };

  return (
    <div className="header__lang-switcher" ref={containerRef}>
      <button
        type="button"
        className="header__lang"
        aria-label="Change language"
        aria-haspopup="true"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prevIsOpen) => !prevIsOpen)}
      >
        {currentOption.flag}
        <span className="header__lang-chevron" data-open={isOpen} />
      </button>

      {isOpen && (
        <ul className="header__lang-list">
          {languageOptions.map(({ id, label, flag }) => (
            <li key={id}>
              <button
                type="button"
                className="header__lang-option"
                data-active={id === currentOption.id}
                onClick={() => onLanguageSelect(id)}
              >
                {flag}
                <span>{label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
